import { Text, Skeleton } from '@mantine/core'
import { type ReactQuery } from 'cms-api'
import Link from 'next/link'
import React from 'react'
import { ROUTE } from 'shared-constants'
import { Button, BUTTON_VARIANT, BUTTON_ICON_POSITION } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import Icon from 'components/Icon/Icon'
import { Box, Flex, Grid } from 'components/Layout'
import { COLOR_NAMES, SPACE } from 'Theme'
import { TIER_LADDER, fmtThresholdK, type Tier } from '../tierLadder'

const GREEN = COLOR_NAMES.PINEY_LAKE
const INK = COLOR_NAMES.EEIRE_BLACK
const MUTED = COLOR_NAMES.GRAY_SHINGLE
const LINE = COLOR_NAMES.JUPITER

const MS_PER_DAY = 86400000

const formatDate = (d: Date) =>
  d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

type StatusLifecycleWidgetProps = {
  currentTier?: Tier
  currentYearSpend?: number
  spendToNextTier: ReactQuery.ExpertDashboardTradeTierResponse['spendToNextTier']
  isLoading?: boolean
}

export const StatusLifecycleWidget = ({
  currentTier,
  currentYearSpend = 0,
  spendToNextTier,
  isLoading,
}: StatusLifecycleWidgetProps) => {
  const today = new Date()
  const curYear = today.getFullYear()

  const rawIdx = TIER_LADDER.findIndex((t) => t.name === currentTier)
  const currentIdx = rawIdx < 0 ? 0 : rawIdx
  const current = TIER_LADDER[currentIdx]
  const next = TIER_LADDER[currentIdx + 1] ?? null
  const ytdSpend = currentYearSpend

  // Status earned/secured this calendar year when this year's spend has reached
  // the current tier's threshold (vs. a tier carried over from a prior year).
  const isEarnedThisYear = currentIdx > 0 && ytdSpend >= current.threshold
  const isMaintained = ytdSpend >= current.threshold

  // Earn → benefit-through model: status earned in year Y is active through the
  // end of year Y+1.
  const earnYear = isEarnedThisYear || currentIdx === 0 ? curYear : curYear - 1
  const activeThrough = new Date(earnYear + 1, 11, 31)
  const reQualifyBy = activeThrough

  const daysRemaining = Math.max(
    0,
    Math.round((activeThrough.getTime() - today.getTime()) / MS_PER_DAY)
  )
  const yearEnd = new Date(curYear, 11, 31)
  const daysToYearEnd = Math.max(
    0,
    Math.round((yearEnd.getTime() - today.getTime()) / MS_PER_DAY)
  )

  const gapToLevelUp = next
    ? (spendToNextTier ?? Math.max(0, next.threshold - ytdSpend))
    : 0
  const gapToMaintain = Math.max(0, current.threshold - ytdSpend)

  // Header status badge
  let badgeLabel: string
  if (!next) {
    badgeLabel = 'Top tier secured'
  } else if (isEarnedThisYear) {
    badgeLabel = `Leveled up to ${current.name} this year`
  } else if (isMaintained) {
    badgeLabel = `${current.name} re-earned for next year`
  } else {
    badgeLabel = `Push to lock in ${current.name} for next year`
  }

  // 01 Earned
  let earnSub: string
  if (currentIdx === 0) {
    earnSub = 'Default tier — no qualifying spend required'
  } else if (isEarnedThisYear) {
    earnSub = `Crossed ${fmtThresholdK(current.threshold)} threshold with ${earnYear} spend`
  } else {
    earnSub = `${earnYear} spend (counter has since reset)`
  }

  // 03 Re-qualify by
  let reQualifySub: string
  if (!next) {
    reQualifySub = `${current.name} maintained for life`
  } else if (isEarnedThisYear) {
    reQualifySub = `${current.name} locked through ${reQualifyBy.getFullYear()} · ${fmtThresholdK(gapToLevelUp)} more in ${curYear} for ${next.name} perks through ${reQualifyBy.getFullYear()}`
  } else if (isMaintained) {
    reQualifySub = `${current.name} re-earned in ${reQualifyBy.getFullYear()} · ${fmtThresholdK(gapToLevelUp)} more for ${next.name}`
  } else {
    reQualifySub = `${fmtThresholdK(gapToMaintain)} more in ${reQualifyBy.getFullYear()} locks in ${current.name} for ${reQualifyBy.getFullYear() + 1}`
  }

  // Pacing forecast
  let pacingLine: string
  if (!next) {
    pacingLine = `You've reached the top tier. Lifetime ${current.name} means no annual re-qualification — every perk stays unlocked.`
  } else if (isEarnedThisYear) {
    pacingLine = `${current.name} status is unlocked through ${activeThrough.getFullYear()}! You have ${daysToYearEnd} days to spend ${fmtThresholdK(gapToLevelUp)} more to unlock ${next.name}.`
  } else if (isMaintained) {
    pacingLine = `${current.name} is re-qualified for ${activeThrough.getFullYear()}. ${fmtThresholdK(gapToLevelUp)} more this year stretches you up to ${next.name}.`
  } else {
    pacingLine = `${fmtThresholdK(gapToMaintain)} more in ${curYear} locks in ${current.name} for ${activeThrough.getFullYear()} — you've got ${daysToYearEnd} days to make it happen.`
  }

  const steps = [
    {
      n: '01',
      label: 'Earned',
      value: `${current.name} · ${earnYear}`,
      sub: earnSub,
    },
    {
      n: '02',
      label: 'Active through',
      value: formatDate(activeThrough),
      sub: `${daysRemaining} days of ${current.name} perks remaining · your ${earnYear} status carries over`,
    },
    {
      n: '03',
      label: 'Re-qualify by',
      value: formatDate(reQualifyBy),
      sub: reQualifySub,
    },
  ]

  if (isLoading) {
    return (
      <Card
        flexDirection="column"
        width="100%"
        elevation={CARD_ELEVATION.NONE}
        hasBorders
        paddingSize={CARD_PADDING_SIZE.NONE}
      >
        <Box p={SPACE.PX_30} width="100%">
          <Skeleton height={16} width="30%" mb={10} />
          <Skeleton height={28} width="50%" mb={10} />
          <Skeleton height={40} width="80%" mb={30} />
          <Skeleton height={80} radius="md" />
        </Box>
      </Card>
    )
  }

  return (
    <Card
      flexDirection="column"
      width="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
    >
      <Box p={SPACE.PX_30} width="100%">
        {/* Header */}
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          gap={SPACE.PX_16}
          flexWrap="wrap"
        >
          <Box style={{ maxWidth: 640 }}>
            <Text
              tt="uppercase"
              fz={11}
              fw={600}
              c={MUTED}
              style={{ letterSpacing: '0.14em' }}
            >
              How your status works
            </Text>
            <Text
              fz={26}
              fw={500}
              c={INK}
              mt={SPACE.PX_4}
              style={{ lineHeight: 1.15 }}
            >
              Earn it one year.{' '}
              <Text component="span" fz={26} fw={500} fs="italic" c={INK}>
                Enjoy it through the next.
              </Text>
            </Text>
            <Text fz={13} c={MUTED} mt={SPACE.PX_8} style={{ lineHeight: 1.5 }}>
              Your spend in a calendar year determines your status — and once
              you&apos;ve earned it, that status stays active through the end of
              the following year.{' '}
              <Text
                component="span"
                fz={13}
                fw={600}
                c={COLOR_NAMES.CARBON_COPY}
              >
                Every January 1st, your qualifying spend resets to $0
              </Text>
              , so hit your threshold again by December 31st to maintain (or
              level up) your status and keep the benefits rolling.
            </Text>
          </Box>
          <Flex
            alignItems="center"
            gap={SPACE.PX_8}
            px={SPACE.PX_12}
            py={SPACE.PX_8}
            backgroundColor={COLOR_NAMES.TETON_BREEZE}
            style={{ borderRadius: 99, whiteSpace: 'nowrap' }}
          >
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: GREEN,
              }}
            />
            <Text fz={12} fw={600} c={GREEN}>
              {badgeLabel}
            </Text>
          </Flex>
        </Flex>

        {/* Three-step summary strip */}
        <Grid
          gridTemplateColumns={{ MOBILE: '1fr', TABLET: '1fr 1fr 1fr' }}
          mt={SPACE.PX_20}
          borderTop={`1px solid ${LINE}`}
          borderBottom={`1px solid ${LINE}`}
        >
          {steps.map((s, i) => (
            <Flex
              key={s.n}
              gap={SPACE.PX_12}
              alignItems="flex-start"
              py={SPACE.PX_16}
              pr={{ MOBILE: 0, TABLET: 20 }}
              pl={{ MOBILE: 0, TABLET: i === 0 ? 0 : 20 }}
              borderLeft={{
                MOBILE: 'none',
                TABLET: i === 0 ? 'none' : `1px solid ${LINE}`,
              }}
              borderTop={{
                MOBILE: i === 0 ? 'none' : `1px solid ${LINE}`,
                TABLET: 'none',
              }}
            >
              <Text
                fz={11}
                fw={600}
                c={GREEN}
                mt={SPACE.PX_4}
                style={{
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {s.n}
              </Text>
              <Box style={{ minWidth: 0 }}>
                <Text
                  tt="uppercase"
                  fz={11}
                  fw={600}
                  c={MUTED}
                  style={{ letterSpacing: '0.12em' }}
                >
                  {s.label}
                </Text>
                <Text
                  fz={20}
                  fw={500}
                  c={INK}
                  mt={SPACE.PX_4}
                  style={{ lineHeight: 1.1 }}
                >
                  {s.value}
                </Text>
                <Text fz={12} c={MUTED} mt={SPACE.PX_4}>
                  {s.sub}
                </Text>
              </Box>
            </Flex>
          ))}
        </Grid>

        {/* Pacing forecast */}
        <Flex
          mt={SPACE.PX_20}
          p={SPACE.PX_16}
          alignItems="center"
          justifyContent="space-between"
          gap={SPACE.PX_16}
          flexWrap="wrap"
          backgroundColor={COLOR_NAMES.DOCTOR}
          style={{ borderRadius: SPACE.PX_12, border: `1px solid ${LINE}` }}
        >
          <Flex alignItems="center" gap={SPACE.PX_12} style={{ minWidth: 0 }}>
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: COLOR_NAMES.TETON_BREEZE,
                flex: 'none',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={GREEN}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 17 L9 11 L13 15 L21 7" />
                <path d="M15 7 L21 7 L21 13" />
              </svg>
            </Flex>
            <Box style={{ minWidth: 0 }}>
              <Text
                tt="uppercase"
                fz={11}
                fw={600}
                c={MUTED}
                style={{ letterSpacing: '0.12em' }}
              >
                Pacing forecast
              </Text>
              <Text
                fz={14}
                c={COLOR_NAMES.CARBON_COPY}
                mt={SPACE.PX_4}
                style={{ lineHeight: 1.4 }}
              >
                {pacingLine}
              </Text>
            </Box>
          </Flex>
          <Link href={ROUTE.SHOWROOM()}>
            <Button
              variant={BUTTON_VARIANT.FILLED}
              icon={Icon.ArrowRight}
              iconPosition={BUTTON_ICON_POSITION.RIGHT}
            >
              Keep shopping
            </Button>
          </Link>
        </Flex>
      </Box>
    </Card>
  )
}
